export const PLACEMENT_DURATION_OPTIONS = [12] as const
export const MINIMUM_PLACEMENT_DURATION_MONTHS = 12 as const
export const PLACEMENT_TERM_OPTIONS = ['12-month', 'campaign-lifetime'] as const
export const DEFAULT_PLACEMENT_TERM = '12-month' as const
export const MINIMUM_PRINT_WIDTH_INCHES = 3
export const MINIMUM_PRINT_HEIGHT_INCHES = 3
export const FREEFORM_SURFACE_ANCHOR_REVISION =
  'model-x-closed.glb@sha256:09a2c47e2f281eb37e19242caa99fa1d861cc66f18fb27231c47d5b8286671f6' as const

export const MONTHLY_RATE_USD_PER_SQUARE_FOOT = 1_200
export const PRODUCTION_SETUP_ESTIMATE_USD = 75
export const PRODUCTION_ESTIMATE_USD_PER_SQUARE_FOOT = 25

/**
 * Provisional campaign-lifetime pricing uses one physical-area curve across
 * every panel. The endpoints are the current 3 x 3 inch print minimum and the
 * 52 x 42 inch hood planning reference. Sponsorship rounds to the nearest
 * whole dollar; production remains a separate estimate.
 */
export const CAMPAIGN_LIFETIME_MINIMUM_AREA_SQUARE_INCHES = 9
export const CAMPAIGN_LIFETIME_MAXIMUM_AREA_SQUARE_INCHES = 2_184
export const CAMPAIGN_LIFETIME_MINIMUM_SPONSORSHIP_USD = 50_000
export const CAMPAIGN_LIFETIME_MAXIMUM_SPONSORSHIP_USD = 100_000

export type PlacementDurationMonths =
  (typeof PLACEMENT_DURATION_OPTIONS)[number]

export type PlacementTerm = (typeof PLACEMENT_TERM_OPTIONS)[number]

export const PLACEMENT_TERM_DISCLOSURES = {
  '12-month':
    'Local 12-month proposal estimate only. Panel dimensions are unmeasured; final size, price, production cost, availability, and installation require owner and installer review. This planning tool does not provide checkout, reserve inventory, publish artwork, or take payment.',
  'campaign-lifetime':
    'Local Permanent campaign placement estimate only. Permanent means the operational life of this specific campaign vehicle and owner project under a reviewed written agreement—not forever, legal perpetuity, a transferable right, or a guarantee binding a future owner. Vehicle sale or transfer, retirement, repaint or rewrap, total loss, campaign closure, maintenance, damage, required removal or replacement, and other written exceptions may end, interrupt, relocate, replace, or otherwise change display. Panel dimensions, final price, production, availability, installation, downtime, credit, and remedies require owner, installer, and written-agreement review. This planning tool does not provide checkout, reserve inventory, publish artwork, or take payment.',
} as const satisfies Record<PlacementTerm, string>

export type PlacementKind = 'image' | 'text'

export type PlacementSurface =
  'front' | 'rear' | 'driver-side' | 'passenger-side'

export const PROVISIONAL_PANEL_IDS = [
  'hood',
  'liftgate',
  'driver-front-fender',
  'driver-front-door',
  'driver-rear-door',
  'driver-rear-quarter',
  'passenger-front-fender',
  'passenger-front-door',
  'passenger-rear-door',
  'passenger-rear-quarter',
] as const

export type ProvisionalPanelId = (typeof PROVISIONAL_PANEL_IDS)[number]

export interface ProvisionalPanelSpec {
  readonly id: ProvisionalPanelId
  readonly surface: PlacementSurface
  /**
   * A planning reference only, not a measured printable area. Every placement
   * still requires installer measurement, artwork preflight, and owner review.
   */
  readonly referenceWidthInches: number
  readonly referenceHeightInches: number
  readonly measurementStatus: 'provisional-unmeasured'
}

export const PROVISIONAL_PANEL_SPECS = {
  hood: panel('hood', 'front', 52, 42),
  liftgate: panel('liftgate', 'rear', 44, 25),
  'driver-front-fender': panel('driver-front-fender', 'driver-side', 27, 18),
  'driver-front-door': panel('driver-front-door', 'driver-side', 43, 31),
  'driver-rear-door': panel('driver-rear-door', 'driver-side', 38, 31),
  'driver-rear-quarter': panel('driver-rear-quarter', 'driver-side', 34, 23),
  'passenger-front-fender': panel(
    'passenger-front-fender',
    'passenger-side',
    27,
    18,
  ),
  'passenger-front-door': panel(
    'passenger-front-door',
    'passenger-side',
    43,
    31,
  ),
  'passenger-rear-door': panel('passenger-rear-door', 'passenger-side', 38, 31),
  'passenger-rear-quarter': panel(
    'passenger-rear-quarter',
    'passenger-side',
    34,
    23,
  ),
} as const satisfies Record<ProvisionalPanelId, ProvisionalPanelSpec>

export interface Placement {
  readonly id: string
  readonly kind: PlacementKind
  /** Plain text, or a local `blob:`/`data:image/` preview URL for an image. */
  readonly content: string
  /** Normalized center position within the provisional panel. */
  readonly x: number
  /** Normalized center position within the provisional panel. */
  readonly y: number
  /** Normalized fraction of the provisional panel width. */
  readonly width: number
  /** Normalized fraction of the provisional panel height. */
  readonly height: number
  /** Clockwise rotation within the panel, normalized to -180...180 degrees. */
  readonly rotationDegrees: number
  /** Requested physical dimensions drive the free-surface concept estimate. */
  readonly requestedWidthInches?: number
  readonly requestedHeightInches?: number
  /**
   * Stable model-local hit and projector record. When present, this is the
   * rendering source of truth; legacy panel-local x/y remain only for older
   * proposal records and precision-control compatibility.
   */
  readonly surfaceAnchor?: PlacementSurfaceAnchor
  readonly surface: PlacementSurface
  readonly panel: ProvisionalPanelId
  readonly term: PlacementTerm
  /** Null when `term` is campaign-lifetime; Permanent is not a month count. */
  readonly durationMonths: PlacementDurationMonths | null
}

export interface PlacementSurfaceAnchor {
  readonly schemaVersion: 1
  readonly vehicleView: 'open' | 'closed'
  readonly modelAssetRevision: typeof FREEFORM_SURFACE_ANCHOR_REVISION
  readonly position: readonly [number, number, number]
  readonly normal: readonly [number, number, number]
  readonly tangent: readonly [number, number, number]
  readonly quaternion: readonly [number, number, number, number]
  readonly projectionSize: {
    readonly width: number
    readonly height: number
    readonly depth: number
  }
  readonly rotationDegrees: number
  readonly normalThreshold: number
  readonly target: {
    readonly objectName: string
    readonly materialName: string
    readonly faceIndex: number
    readonly barycentric: readonly [number, number, number]
  }
}

interface PlacementReviewRecordBase {
  readonly recordId: string
  readonly placement: Placement
}

export interface DraftPlacementRecord extends PlacementReviewRecordBase {
  readonly reviewStatus: 'draft'
}

export interface SubmittedPlacementRecord extends PlacementReviewRecordBase {
  readonly reviewStatus: 'submitted'
  readonly submittedAt: string
}

export interface AcceptedPlacementRecord extends PlacementReviewRecordBase {
  readonly reviewStatus: 'accepted'
  readonly submittedAt: string
  readonly reviewedAt: string
  /** Opaque reference to the trusted owner/admin review record. */
  readonly reviewReference: string
}

export interface RejectedPlacementRecord extends PlacementReviewRecordBase {
  readonly reviewStatus: 'rejected'
  readonly submittedAt: string
  readonly reviewedAt: string
  readonly rejectionReason: string
}

export type PlacementReviewRecord =
  | DraftPlacementRecord
  | SubmittedPlacementRecord
  | AcceptedPlacementRecord
  | RejectedPlacementRecord

export type PlacementEstimateStatus =
  'provisional-installer-review-required' | 'below-minimum-print-size'

export type PlacementValidationCode =
  | 'invalid-id'
  | 'unsupported-kind'
  | 'empty-content'
  | 'non-local-image-content'
  | 'invalid-normalized-position'
  | 'invalid-normalized-size'
  | 'invalid-rotation'
  | 'invalid-requested-size'
  | 'invalid-surface-anchor'
  | 'outside-panel-bounds'
  | 'unsupported-panel'
  | 'surface-panel-mismatch'
  | 'unsupported-term'
  | 'unsupported-duration'
  | 'below-minimum-print-width'
  | 'below-minimum-print-height'

export interface PlacementValidationIssue {
  readonly code: PlacementValidationCode
  readonly message: string
}

export interface PlacementEstimate {
  readonly status: PlacementEstimateStatus
  readonly term: PlacementTerm
  readonly durationMonths: PlacementDurationMonths | null
  readonly estimatedWidthInches: number
  readonly estimatedHeightInches: number
  readonly estimatedAreaSquareInches: number
  readonly estimatedAreaSquareFeet: number
  readonly monthlyRateUsd: number | null
  readonly sponsorshipSubtotalUsd: number
  readonly productionEstimateUsd: number
  readonly totalEstimateUsd: number
  readonly validationIssues: readonly PlacementValidationIssue[]
  readonly disclosure: string
}

export type PlacementUpdate = Partial<Omit<Placement, 'id'>>

export interface PlacementPanelPoint {
  readonly x: number
  readonly y: number
}

export class PlacementValidationError extends Error {
  readonly issues: readonly PlacementValidationIssue[]

  constructor(issues: readonly PlacementValidationIssue[]) {
    super(issues.map((issue) => issue.message).join(' '))
    this.name = 'PlacementValidationError'
    this.issues = issues
  }
}

export function estimateCampaignLifetimeSponsorshipUsd(
  areaSquareInches: number,
): number {
  if (!Number.isFinite(areaSquareInches)) {
    throw new RangeError('Placement area must be a finite number.')
  }

  const areaRatio = clamp(
    (areaSquareInches - CAMPAIGN_LIFETIME_MINIMUM_AREA_SQUARE_INCHES) /
      (CAMPAIGN_LIFETIME_MAXIMUM_AREA_SQUARE_INCHES -
        CAMPAIGN_LIFETIME_MINIMUM_AREA_SQUARE_INCHES),
    0,
    1,
  )

  return Math.round(
    CAMPAIGN_LIFETIME_MINIMUM_SPONSORSHIP_USD +
      (CAMPAIGN_LIFETIME_MAXIMUM_SPONSORSHIP_USD -
        CAMPAIGN_LIFETIME_MINIMUM_SPONSORSHIP_USD) *
        areaRatio,
  )
}

export function estimatePlacement(placement: Placement): PlacementEstimate {
  const structuralIssues = validatePlacementStructure(placement)
  if (structuralIssues.length > 0) {
    throw new PlacementValidationError(structuralIssues)
  }

  const panelSpec = PROVISIONAL_PANEL_SPECS[placement.panel]
  const rawWidthInches =
    placement.requestedWidthInches ??
    panelSpec.referenceWidthInches * placement.width
  const rawHeightInches =
    placement.requestedHeightInches ??
    panelSpec.referenceHeightInches * placement.height
  const estimatedWidthInches = roundMeasurement(rawWidthInches)
  const estimatedHeightInches = roundMeasurement(rawHeightInches)
  const rawAreaSquareInches = rawWidthInches * rawHeightInches
  const estimatedAreaSquareInches = roundMeasurement(rawAreaSquareInches)
  const estimatedAreaSquareFeet = roundMeasurement(rawAreaSquareInches / 144)
  const twelveMonthMonthlyRateUsd = roundCurrency(
    (rawAreaSquareInches / 144) * MONTHLY_RATE_USD_PER_SQUARE_FOOT,
  )
  const monthlyRateUsd =
    placement.term === '12-month' ? twelveMonthMonthlyRateUsd : null
  const sponsorshipSubtotalUsd =
    placement.term === '12-month'
      ? roundCurrency(twelveMonthMonthlyRateUsd * 12)
      : estimateCampaignLifetimeSponsorshipUsd(rawAreaSquareInches)
  const productionEstimateUsd = roundCurrency(
    PRODUCTION_SETUP_ESTIMATE_USD +
      (rawAreaSquareInches / 144) * PRODUCTION_ESTIMATE_USD_PER_SQUARE_FOOT,
  )
  const minimumSizeIssues = validateMinimumPrintSize(
    rawWidthInches,
    rawHeightInches,
  )

  return {
    status:
      minimumSizeIssues.length > 0
        ? 'below-minimum-print-size'
        : 'provisional-installer-review-required',
    term: placement.term,
    durationMonths: placement.durationMonths,
    estimatedWidthInches,
    estimatedHeightInches,
    estimatedAreaSquareInches,
    estimatedAreaSquareFeet,
    monthlyRateUsd,
    sponsorshipSubtotalUsd,
    productionEstimateUsd,
    totalEstimateUsd: roundCurrency(
      sponsorshipSubtotalUsd + productionEstimateUsd,
    ),
    validationIssues: minimumSizeIssues,
    disclosure: PLACEMENT_TERM_DISCLOSURES[placement.term],
  }
}

export function validatePlacement(
  placement: Placement,
): readonly PlacementValidationIssue[] {
  const structuralIssues = validatePlacementStructure(placement)
  if (structuralIssues.length > 0) return structuralIssues

  return estimatePlacement(placement).validationIssues
}

/**
 * Fail-closed campaign publication boundary. Accepted records must come from a
 * trusted owner/admin review workflow; this local domain intentionally exposes
 * no transition or self-approval function.
 */
export function selectAcceptedPlacementsForRenderer(
  records: readonly PlacementReviewRecord[],
): readonly Placement[] {
  return records.flatMap((record) =>
    record.reviewStatus === 'accepted' &&
    validatePlacement(record.placement).length === 0
      ? [{ ...record.placement }]
      : [],
  )
}

export function addPlacement(
  placements: readonly Placement[],
  placement: Placement,
): readonly Placement[] {
  if (placements.some((item) => item.id === placement.id)) {
    throw new Error(`Placement ID "${placement.id}" already exists.`)
  }

  assertValidPlacement(placement)
  return [...placements, { ...placement }]
}

export function updatePlacement(
  placements: readonly Placement[],
  placementId: string,
  update: PlacementUpdate,
): readonly Placement[] {
  const placementIndex = placements.findIndex((item) => item.id === placementId)
  if (placementIndex < 0) {
    throw new Error(`Placement ID "${placementId}" does not exist.`)
  }

  const updatedPlacement = { ...placements[placementIndex], ...update }
  assertValidPlacement(updatedPlacement)

  return placements.map((placement, index) =>
    index === placementIndex ? updatedPlacement : placement,
  )
}

export function removePlacement(
  placements: readonly Placement[],
  placementId: string,
): readonly Placement[] {
  if (!placements.some((item) => item.id === placementId)) {
    throw new Error(`Placement ID "${placementId}" does not exist.`)
  }

  return placements.filter((placement) => placement.id !== placementId)
}

export function movePlacementFromPanelPoints(
  placement: Placement,
  start: PlacementPanelPoint,
  current: PlacementPanelPoint,
): PlacementUpdate {
  const nextCenter = clampPlacementCenter(
    placement,
    placement.x + current.x - start.x,
    placement.y + current.y - start.y,
  )
  return { x: nextCenter.x, y: nextCenter.y }
}

export function resizePlacementFromPanelPoints(
  placement: Placement,
  start: PlacementPanelPoint,
  current: PlacementPanelPoint,
): PlacementUpdate {
  const panelSpec = PROVISIONAL_PANEL_SPECS[placement.panel]
  const radians = (placement.rotationDegrees * Math.PI) / 180
  const deltaXInches = (current.x - start.x) * panelSpec.referenceWidthInches
  const deltaYInches = (current.y - start.y) * panelSpec.referenceHeightInches
  const localDeltaX =
    Math.cos(radians) * deltaXInches + Math.sin(radians) * deltaYInches
  const localDeltaY =
    -Math.sin(radians) * deltaXInches + Math.cos(radians) * deltaYInches
  let width = clamp(
    placement.width + (2 * localDeltaX) / panelSpec.referenceWidthInches,
    MINIMUM_PRINT_WIDTH_INCHES / panelSpec.referenceWidthInches,
    1,
  )
  let height = clamp(
    placement.height + (2 * localDeltaY) / panelSpec.referenceHeightInches,
    MINIMUM_PRINT_HEIGHT_INCHES / panelSpec.referenceHeightInches,
    1,
  )

  const halfExtents = rotatedPlacementHalfExtents({
    ...placement,
    width,
    height,
  })
  const availableX = Math.min(placement.x, 1 - placement.x)
  const availableY = Math.min(placement.y, 1 - placement.y)
  const scale = Math.min(
    1,
    halfExtents.x > 0 ? availableX / halfExtents.x : 1,
    halfExtents.y > 0 ? availableY / halfExtents.y : 1,
  )
  width *= scale
  height *= scale
  return { width, height }
}

export function rotatePlacementFromPanelPoints(
  placement: Placement,
  start: PlacementPanelPoint,
  current: PlacementPanelPoint,
): PlacementUpdate {
  const panelSpec = PROVISIONAL_PANEL_SPECS[placement.panel]
  const angleAt = (point: PlacementPanelPoint) =>
    Math.atan2(
      (point.y - placement.y) * panelSpec.referenceHeightInches,
      (point.x - placement.x) * panelSpec.referenceWidthInches,
    )
  const deltaDegrees = ((angleAt(current) - angleAt(start)) * 180) / Math.PI
  const rotationDegrees = normalizeRotationDegrees(
    placement.rotationDegrees + deltaDegrees,
  )
  const nextCenter = clampPlacementCenter(
    { ...placement, rotationDegrees },
    placement.x,
    placement.y,
  )
  return { rotationDegrees, x: nextCenter.x, y: nextCenter.y }
}

export function setPlacementRotationDegrees(
  placement: Placement,
  value: number,
): PlacementUpdate {
  const rotationDegrees = normalizeRotationDegrees(value)
  const nextCenter = clampPlacementCenter(
    { ...placement, rotationDegrees },
    placement.x,
    placement.y,
  )
  return { rotationDegrees, x: nextCenter.x, y: nextCenter.y }
}

export function normalizeRotationDegrees(value: number) {
  if (!Number.isFinite(value)) return 0
  return ((((value + 180) % 360) + 360) % 360) - 180
}

function assertValidPlacement(placement: Placement) {
  const issues = validatePlacement(placement)
  if (issues.length > 0) throw new PlacementValidationError(issues)
}

function validatePlacementStructure(
  placement: Placement,
): PlacementValidationIssue[] {
  const issues: PlacementValidationIssue[] = []
  const panelSpec = isProvisionalPanelId(placement.panel)
    ? PROVISIONAL_PANEL_SPECS[placement.panel]
    : undefined

  if (
    typeof placement.id !== 'string' ||
    !/^[a-zA-Z0-9][a-zA-Z0-9_-]*$/.test(placement.id)
  ) {
    issues.push(
      issue('invalid-id', 'Placement ID must be URL-safe and non-empty.'),
    )
  }

  if (
    (placement.requestedWidthInches !== undefined &&
      (!Number.isFinite(placement.requestedWidthInches) ||
        placement.requestedWidthInches <= 0)) ||
    (placement.requestedHeightInches !== undefined &&
      (!Number.isFinite(placement.requestedHeightInches) ||
        placement.requestedHeightInches <= 0)) ||
    (placement.requestedWidthInches === undefined) !==
      (placement.requestedHeightInches === undefined)
  ) {
    issues.push(
      issue(
        'invalid-requested-size',
        'Free-surface requested width and height must be finite positive numbers supplied together.',
      ),
    )
  }

  if (
    placement.surfaceAnchor !== undefined &&
    (!isValidSurfaceAnchor(placement.surfaceAnchor) ||
      Math.abs(
        normalizeRotationDegrees(
          placement.rotationDegrees - placement.surfaceAnchor.rotationDegrees,
        ),
      ) > 0.0001)
  ) {
    issues.push(
      issue(
        'invalid-surface-anchor',
        'Free-surface anchor data must match the current vehicle revision and contain a valid model-local hit basis.',
      ),
    )
  }

  if (placement.kind !== 'image' && placement.kind !== 'text') {
    issues.push(
      issue('unsupported-kind', 'Placement kind must be either image or text.'),
    )
  }

  if (typeof placement.content !== 'string' || !placement.content.trim()) {
    issues.push(issue('empty-content', 'Placement content cannot be empty.'))
  } else if (
    placement.kind === 'image' &&
    !isLocalImagePreviewContent(placement.content)
  ) {
    issues.push(
      issue(
        'non-local-image-content',
        'Image previews must use a local blob or image data URL.',
      ),
    )
  }

  if (!isNormalized(placement.x) || !isNormalized(placement.y)) {
    issues.push(
      issue(
        'invalid-normalized-position',
        'Placement center coordinates must be between 0 and 1.',
      ),
    )
  }

  if (
    !isNormalizedSize(placement.width) ||
    !isNormalizedSize(placement.height)
  ) {
    issues.push(
      issue(
        'invalid-normalized-size',
        'Placement width and height must be greater than 0 and at most 1.',
      ),
    )
  }

  if (
    !Number.isFinite(placement.rotationDegrees) ||
    placement.rotationDegrees < -180 ||
    placement.rotationDegrees > 180
  ) {
    issues.push(
      issue(
        'invalid-rotation',
        'Placement rotation must be between -180 and 180 degrees.',
      ),
    )
  }

  if (
    panelSpec &&
    !placement.surfaceAnchor &&
    isNormalized(placement.x) &&
    isNormalized(placement.y) &&
    isNormalizedSize(placement.width) &&
    isNormalizedSize(placement.height) &&
    Number.isFinite(placement.rotationDegrees) &&
    (() => {
      const halfExtents = rotatedPlacementHalfExtents(placement)
      return (
        placement.x - halfExtents.x < 0 ||
        placement.x + halfExtents.x > 1 ||
        placement.y - halfExtents.y < 0 ||
        placement.y + halfExtents.y > 1
      )
    })()
  ) {
    issues.push(
      issue(
        'outside-panel-bounds',
        'The complete placement must remain inside its provisional panel.',
      ),
    )
  }

  if (!panelSpec) {
    issues.push(
      issue(
        'unsupported-panel',
        'Placement must use a supported provisional panel.',
      ),
    )
  } else if (panelSpec.surface !== placement.surface) {
    issues.push(
      issue(
        'surface-panel-mismatch',
        'Placement surface must match its provisional panel.',
      ),
    )
  }

  if (!isPlacementTerm(placement.term)) {
    issues.push(
      issue(
        'unsupported-term',
        `Placement term must be one of: ${PLACEMENT_TERM_OPTIONS.join(', ')}.`,
      ),
    )
  }

  if (
    (placement.term === '12-month' &&
      !isPlacementDuration(placement.durationMonths)) ||
    (placement.term === 'campaign-lifetime' &&
      placement.durationMonths !== null)
  ) {
    issues.push(
      issue(
        'unsupported-duration',
        '12-month placements require a 12-month duration; campaign-lifetime placements require a null month duration.',
      ),
    )
  }

  return issues
}

function isValidSurfaceAnchor(anchor: PlacementSurfaceAnchor) {
  const barycentricSum = anchor.target.barycentric.reduce(
    (sum, value) => sum + value,
    0,
  )
  return (
    anchor.schemaVersion === 1 &&
    anchor.vehicleView === 'closed' &&
    anchor.modelAssetRevision === FREEFORM_SURFACE_ANCHOR_REVISION &&
    Boolean(anchor.target.objectName.trim()) &&
    Boolean(anchor.target.materialName.trim()) &&
    Number.isInteger(anchor.target.faceIndex) &&
    anchor.target.faceIndex >= 0 &&
    isFiniteTuple3(anchor.target.barycentric) &&
    anchor.target.barycentric.every(
      (value) => value >= -0.0001 && value <= 1.0001,
    ) &&
    Math.abs(barycentricSum - 1) <= 0.001 &&
    isFiniteTuple3(anchor.position) &&
    isFiniteUnitDirection(anchor.normal) &&
    isFiniteUnitDirection(anchor.tangent) &&
    isFiniteUnitQuaternion(anchor.quaternion) &&
    Math.abs(
      anchor.normal[0] * anchor.tangent[0] +
        anchor.normal[1] * anchor.tangent[1] +
        anchor.normal[2] * anchor.tangent[2],
    ) < 0.002 &&
    Number.isFinite(anchor.projectionSize.width) &&
    anchor.projectionSize.width > 0 &&
    Number.isFinite(anchor.projectionSize.height) &&
    anchor.projectionSize.height > 0 &&
    Number.isFinite(anchor.projectionSize.depth) &&
    anchor.projectionSize.depth > 0 &&
    Number.isFinite(anchor.rotationDegrees) &&
    anchor.rotationDegrees >= -180 &&
    anchor.rotationDegrees <= 180 &&
    Number.isFinite(anchor.normalThreshold) &&
    anchor.normalThreshold >= -1 &&
    anchor.normalThreshold <= 1
  )
}

function isFiniteTuple3(value: readonly [number, number, number]): boolean {
  return value.length === 3 && value.every(Number.isFinite)
}

function isFiniteTuple4(
  value: readonly [number, number, number, number],
): boolean {
  return value.length === 4 && value.every(Number.isFinite)
}

function isFiniteUnitQuaternion(
  value: readonly [number, number, number, number],
) {
  if (!isFiniteTuple4(value)) return false
  const length = Math.hypot(value[0], value[1], value[2], value[3])
  return length > 0.98 && length < 1.02
}

function isFiniteUnitDirection(value: readonly [number, number, number]) {
  if (!isFiniteTuple3(value)) return false
  const length = Math.hypot(value[0], value[1], value[2])
  return length > 0.98 && length < 1.02
}

function validateMinimumPrintSize(
  widthInches: number,
  heightInches: number,
): PlacementValidationIssue[] {
  const issues: PlacementValidationIssue[] = []

  if (widthInches < MINIMUM_PRINT_WIDTH_INCHES) {
    issues.push(
      issue(
        'below-minimum-print-width',
        `Estimated printed width must be at least ${MINIMUM_PRINT_WIDTH_INCHES} inches.`,
      ),
    )
  }

  if (heightInches < MINIMUM_PRINT_HEIGHT_INCHES) {
    issues.push(
      issue(
        'below-minimum-print-height',
        `Estimated printed height must be at least ${MINIMUM_PRINT_HEIGHT_INCHES} inches.`,
      ),
    )
  }

  return issues
}

function panel(
  id: ProvisionalPanelId,
  surface: PlacementSurface,
  referenceWidthInches: number,
  referenceHeightInches: number,
): ProvisionalPanelSpec {
  return {
    id,
    surface,
    referenceWidthInches,
    referenceHeightInches,
    measurementStatus: 'provisional-unmeasured',
  }
}

function issue(
  code: PlacementValidationCode,
  message: string,
): PlacementValidationIssue {
  return { code, message }
}

function isNormalized(value: number) {
  return Number.isFinite(value) && value >= 0 && value <= 1
}

function isNormalizedSize(value: number) {
  return Number.isFinite(value) && value > 0 && value <= 1
}

function isPlacementDuration(
  value: number | null,
): value is PlacementDurationMonths {
  return PLACEMENT_DURATION_OPTIONS.some((duration) => duration === value)
}

function isPlacementTerm(value: string): value is PlacementTerm {
  return PLACEMENT_TERM_OPTIONS.some((term) => term === value)
}

function isProvisionalPanelId(value: string): value is ProvisionalPanelId {
  return PROVISIONAL_PANEL_IDS.some((panelId) => panelId === value)
}

function isLocalImagePreviewContent(value: string) {
  return value.startsWith('blob:') || value.startsWith('data:image/')
}

function clampPlacementCenter(placement: Placement, x: number, y: number) {
  const halfExtents = rotatedPlacementHalfExtents(placement)
  return {
    x: clamp(x, halfExtents.x, 1 - halfExtents.x),
    y: clamp(y, halfExtents.y, 1 - halfExtents.y),
  }
}

function rotatedPlacementHalfExtents(placement: Placement) {
  const panelSpec = PROVISIONAL_PANEL_SPECS[placement.panel]
  const radians = (placement.rotationDegrees * Math.PI) / 180
  const cosine = Math.abs(Math.cos(radians))
  const sine = Math.abs(Math.sin(radians))
  const panelAspect =
    panelSpec.referenceWidthInches / panelSpec.referenceHeightInches
  return {
    x: (cosine * placement.width + sine * (placement.height / panelAspect)) / 2,
    y: (sine * placement.width * panelAspect + cosine * placement.height) / 2,
  }
}

function clamp(value: number, minimum: number, maximum: number) {
  return Math.min(Math.max(value, minimum), maximum)
}

function roundMeasurement(value: number) {
  return Number(value.toFixed(2))
}

function roundCurrency(value: number) {
  return Number(value.toFixed(2))
}
